import type { PlayerInfo } from '@halvard/protocol';
import type { ice } from '@halvard/rules';
import { t } from '../../i18n';

interface Players {
  host: PlayerInfo | null;
  guest: PlayerInfo | null;
}

function nameOf(players: Players, seat: ice.Seat, me: ice.Seat | null): string {
  const m = t().match;
  if (seat === me) return m.you;
  return players[seat]?.name ?? (seat === 'host' ? m.host : m.opponent);
}

/** "Guest 4F2A reached 20 Glory in round 14" / "You were held to 17 Glory". */
export function attackText(game: ice.IceMatchGame, players: Players, me: ice.Seat | null): string {
  const m = t().match;
  const who = nameOf(players, game.attacker, me);
  if (game.reason === 'resign') return m.resigned(who, game.winner === 'brand', game.round);
  if (game.winner === 'brand') {
    return game.reason === 'sunk'
      ? m.sankAll(who, game.round)
      : m.reached(who, game.glory, game.round);
  }
  return game.reason === 'sunk'
    ? m.lostAll(who, game.round)
    : m.heldTo(who, game.attacker === me, game.glory);
}

/** The match result, from the point of view of `me` (null for spectators). */
export function outcomeLine(
  outcome: ice.IceMatchOutcome,
  games: readonly ice.IceMatchGame[],
  players: Players,
  me: ice.Seat | null,
): { title: string; detail: string } {
  const m = t().match;
  const title =
    outcome.winner === null
      ? m.drawn
      : outcome.winner === me
        ? m.youWin
        : me
          ? m.youLose
          : m.theyWin(players[outcome.winner]?.name ?? m.theWinner);
  const [a, b] = games;
  switch (outcome.reason) {
    case 'resign':
      return { title, detail: m.byResign };
    case 'attack':
      return { title, detail: b ? m.byAttackOne : m.byAttackSingle };
    case 'faster':
      return {
        title,
        detail:
          a && b && a.round !== b.round
            ? m.fasterRounds(Math.min(a.round, b.round), Math.max(a.round, b.round))
            : m.fasterSame,
      };
    case 'glory':
      return {
        title,
        detail:
          a && b
            ? m.moreGlory(Math.max(a.glory, b.glory), Math.min(a.glory, b.glory))
            : m.moreGlorySingle,
      };
    case 'level':
      return { title, detail: m.level };
  }
}
